import {
	CONDITION_OPERATOR_LABELS,
	type ConditionOperator,
	isTagRule,
	TAG_RULE_OPERATOR_LABELS,
	TAG_RULE_OPERATORS,
	type TagRuleOperator,
	UNARY_CONDITION_OPERATORS,
} from "@velizhanin-dev/bot-scenario"
import { Plus, Trash2 } from "lucide-react"
import { draftCache } from "@/entities/bot-builder"
import { Button } from "@/shared/ui/button"
import { Field, IssueText } from "@/shared/ui/form-field"
import { NativeSelect } from "@/shared/ui/native-select"
import { TextWithVariables } from "@/shared/ui/text-with-variables"
import { VariableSelect } from "@/shared/ui/variable-select"
import type { EditorProps } from "../editor-props"
import { issuesAt } from "../issues-text"

interface VariableRuleDraft {
	variable: string
	op: ConditionOperator
	value?: string
}

interface TagRuleDraft {
	kind: "tag"
	tagId: string
	op: TagRuleOperator
}

type RuleDraft = VariableRuleDraft | TagRuleDraft

export function ConditionEditor({ config, onChange, issues, draft }: EditorProps) {
	const rules = (Array.isArray(config.rules) ? config.rules : []) as RuleDraft[]
	const tags = draftCache.pickableTags(
		draft.tags ?? [],
		rules.filter(isTagRule).map((rule) => rule.tagId),
	)
	const setRules = (next: RuleDraft[]) => onChange({ ...config, rules: next })
	const updateRule = (index: number, patch: Partial<VariableRuleDraft>) =>
		setRules(
			rules.map((rule, i) => {
				if (i !== index || isTagRule(rule)) return rule
				const merged: VariableRuleDraft = { ...rule, ...patch }
				if (UNARY_CONDITION_OPERATORS.has(merged.op)) {
					const { value: _value, ...unary } = merged
					return unary
				}
				return { ...merged, value: merged.value ?? "" }
			}),
		)
	const updateTagRule = (index: number, patch: Partial<TagRuleDraft>) =>
		setRules(
			rules.map((rule, i) => (i === index && isTagRule(rule) ? { ...rule, ...patch } : rule)),
		)

	return (
		<div className="space-y-4">
			<Field
				label="Условие выполнено, если"
				hint="Для рабочих часов и выходных: системные переменные hour, weekday, date, time (по Москве)"
			>
				<NativeSelect
					aria-label="Как сочетать правила"
					value={config.match === "any" ? "any" : "all"}
					onChange={(event) => onChange({ ...config, match: event.target.value })}
				>
					<option value="all">выполнены все правила</option>
					<option value="any">выполнено любое правило</option>
				</NativeSelect>
			</Field>
			{rules.map((rule, index) =>
				isTagRule(rule) ? (
					<div key={index} className="space-y-2 rounded-lg border border-border/60 p-2">
						<div className="flex items-center gap-1">
							<NativeSelect
								aria-label={`Группа правила ${index + 1}`}
								value={rule.tagId}
								onChange={(event) => updateTagRule(index, { tagId: event.target.value })}
							>
								<option value="" disabled>
									Выберите группу
								</option>
								{tags.map((tag) => (
									<option key={tag.id} value={tag.id}>
										{tag.name}
									</option>
								))}
							</NativeSelect>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								aria-label={`Удалить правило ${index + 1}`}
								onClick={() => setRules(rules.filter((_, i) => i !== index))}
							>
								<Trash2 />
							</Button>
						</div>
						<NativeSelect
							aria-label={`Проверка группы ${index + 1}`}
							value={rule.op}
							onChange={(event) =>
								updateTagRule(index, { op: event.target.value as TagRuleOperator })
							}
						>
							{TAG_RULE_OPERATORS.map((op) => (
								<option key={op} value={op}>
									{TAG_RULE_OPERATOR_LABELS[op]}
								</option>
							))}
						</NativeSelect>
						<IssueText issues={issuesAt(issues, `rules.${index}`, true)} />
					</div>
				) : (
					<div key={index} className="space-y-2 rounded-lg border border-border/60 p-2">
						<div className="flex items-center gap-1">
							<VariableSelect
								aria-label={`Переменная правила ${index + 1}`}
								value={rule.variable}
								onChange={(variable) => updateRule(index, { variable })}
								variables={draft.variables}
								includeSystem
							/>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								aria-label={`Удалить правило ${index + 1}`}
								onClick={() => setRules(rules.filter((_, i) => i !== index))}
							>
								<Trash2 />
							</Button>
						</div>
						<NativeSelect
							aria-label={`Сравнение правила ${index + 1}`}
							value={rule.op}
							onChange={(event) =>
								updateRule(index, { op: event.target.value as ConditionOperator })
							}
						>
							{(Object.keys(CONDITION_OPERATOR_LABELS) as ConditionOperator[]).map((op) => (
								<option key={op} value={op}>
									{CONDITION_OPERATOR_LABELS[op]}
								</option>
							))}
						</NativeSelect>
						{!UNARY_CONDITION_OPERATORS.has(rule.op) ? (
							<TextWithVariables
								multiline={false}
								placeholder="Значение"
								value={rule.value ?? ""}
								onChange={(value) => updateRule(index, { value })}
								variables={draft.variables}
							/>
						) : null}
						<IssueText issues={issuesAt(issues, `rules.${index}`, true)} />
					</div>
				),
			)}
			<IssueText issues={issuesAt(issues, "rules")} />
			<div className="flex flex-wrap gap-2">
				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={() => setRules([...rules, { variable: "", op: "eq", value: "" }])}
				>
					<Plus />
					Правило по переменной
				</Button>
				{tags.length > 0 ? (
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={() => setRules([...rules, { kind: "tag", tagId: "", op: "has" }])}
					>
						<Plus />
						Правило по группе
					</Button>
				) : null}
			</div>
		</div>
	)
}
